import React, { useEffect, useState } from 'react';
import './EvidenceUploader.css';

/**
 * Reusable "attach photos/documents, preview, delete" widget, intended to be
 * dropped into any page that needs evidence upload (daily log entries,
 * EIA-condition monitoring records, incidents, inspections, etc.) without
 * duplicating the gallery/upload UI.
 *
 * This component knows nothing about the backend shape of the parent entity
 * (MonitoringEntry, MonitoringRecord, Incident, ...) - the caller supplies
 * three async functions that do the actual API work:
 *
 *   fetchEvidence(): Promise<EvidenceItem[]>
 *   uploadEvidence(file, fileType): Promise<EvidenceItem>
 *   deleteEvidence(evidenceId): Promise<void>
 *
 * where EvidenceItem is `{ id, fileUrl, fileType, ... }`.
 *
 * Props:
 *  - isOpen: boolean - whether the modal is visible
 *  - title: string - modal header text
 *  - subtitle: string (optional) - small text under the title
 *  - onClose: () => void
 *  - fetchEvidence / uploadEvidence / deleteEvidence: see above
 *  - reloadKey: any (optional) - change this to force a refetch while open
 *    (e.g. when the caller switches which record the modal is showing)
 *  - readOnly: boolean (optional) - hide the add tile and delete buttons,
 *    for viewers who can see but not modify evidence
 */
const EvidenceUploader = ({
  isOpen,
  title = 'Evidence',
  subtitle,
  onClose,
  fetchEvidence,
  uploadEvidence,
  deleteEvidence,
  reloadKey,
  readOnly = false,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchEvidence();
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load evidence:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reloadKey]);

  if (!isOpen) return null;

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    const fileType = file.type === 'application/pdf' ? 'document' : 'photo';
    try {
      setUploading(true);
      const created = await uploadEvidence(file, fileType);
      setItems((prev) => [...prev, created]);
    } catch (error) {
      console.error('Failed to upload evidence:', error);
      window.alert(error.response?.data?.error || 'Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await deleteEvidence(item.id);
      setItems((prev) => prev.filter((e) => e.id !== item.id));
    } catch (error) {
      console.error('Failed to delete evidence:', error);
      window.alert(error.response?.data?.error || 'Failed to delete evidence');
    }
  };

  const isPdf = (item) => item.fileType === 'document' || (item.fileUrl || '').toLowerCase().endsWith('.pdf');

  return (
    <div className="ev-overlay" onClick={onClose}>
      <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ev-header">
          <div>
            <h2><i className="fas fa-camera"></i> {title}</h2>
            {subtitle && <p className="ev-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="ev-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="ev-body">
          {loading ? (
            <div className="ev-empty">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading evidence...</p>
            </div>
          ) : (
            <div className="ev-grid">
              {items.map((item) => (
                <div key={item.id} className="ev-tile">
                  {isPdf(item) ? (
                    <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="ev-doc-icon">
                      <i className="fas fa-file-pdf"></i>
                    </a>
                  ) : (
                    <a href={item.fileUrl} target="_blank" rel="noopener noreferrer">
                      <img src={item.fileUrl} alt="Evidence" />
                    </a>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      className="ev-remove-btn"
                      onClick={() => handleDelete(item)}
                      aria-label="Delete file"
                      title="Delete"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}

              {!readOnly && (
                <label className="ev-add-tile">
                  <input type="file" accept="image/*,application/pdf" onChange={handleFileSelected} disabled={uploading} />
                  {uploading ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <span>Add photo/PDF</span>
                    </>
                  )}
                </label>
              )}

              {items.length === 0 && readOnly && (
                <div className="ev-empty" style={{ gridColumn: '1 / -1' }}>
                  <i className="fas fa-camera"></i>
                  <p>No evidence attached</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ev-footer">
          <button type="button" className="ev-done-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default EvidenceUploader;
